import React, { useState } from 'react';
import { API_URL } from '../utils/constants';

const BatchProcessor = ({ session, outputType, selectedTemplate, onBatchComplete, onClose }) => {
  const [urls, setUrls] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ total: 0, completed: 0, failed: 0 });

  const handleProcess = async () => {
    const urlList = urls.split('\n').filter(u => u.trim()).map(u => u.trim());

    if (urlList.length === 0) return;
    if (urlList.length > 10) return;

    setProcessing(true);
    setError(null);
    setProgress({ total: urlList.length, completed: 0, failed: 0 });
    setResults([]);

    try {
      const res = await fetch(`${API_URL}/api/batch-process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          urls: urlList,
          outputType,
          template: outputType === 'article' ? selectedTemplate : undefined
        })
      });

      if (res.ok) {
        const data = await res.json();
        setResults(data.results || []);
        setProgress({
          total: data.total || urlList.length,
          completed: data.completed || 0,
          failed: data.failed || 0
        });

        if (onBatchComplete) {
          const successfulProjects = (data.results || []).filter(r => r.success).map(r => r.project);
          onBatchComplete(successfulProjects);
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      console.error('Batch processing failed:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setProcessing(false);
    }
  };

  const urlCount = urls.split('\n').filter(u => u.trim()).length;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(8px)',
      animation: 'fadeIn 0.2s ease', padding: '20px'
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: '20px', padding: '32px', width: '100%',
        maxWidth: '600px', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-color)',
        animation: 'modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)', maxHeight: '90vh', overflow: 'auto'
      }} onClick={e => e.stopPropagation()}>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚡</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
            Batch Transform
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Transform up to 10 YouTube videos at once
          </p>
        </div>

        {!processing && results.length === 0 ? (
          <>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block', fontSize: '14px', fontWeight: '600',
                color: 'var(--text-primary)', marginBottom: '8px'
              }}>
                YouTube URLs (one per line)
              </label>
              <textarea
                value={urls}
                onChange={e => { setUrls(e.target.value); setError(null); }}
                placeholder={"https://youtube.com/watch?v=...\nhttps://youtube.com/watch?v=...\nhttps://youtube.com/watch?v=..."}
                rows={8}
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '10px',
                  border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', fontSize: '14px', outline: 'none',
                  fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical'
                }}
              />
              <div style={{
                fontSize: '12px', color: urlCount > 10 ? 'var(--error)' : 'var(--text-muted)',
                marginTop: '8px'
              }}>
                {urlCount} URL{urlCount !== 1 ? 's' : ''} · Max 10
              </div>
            </div>

            {error && (
              <div style={{
                padding: '14px 16px', borderRadius: '10px', marginBottom: '20px',
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                color: 'var(--error)', fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            <div style={{
              padding: '16px', borderRadius: '10px', marginBottom: '20px',
              background: 'var(--accent-subtle-bg)', border: '1px solid var(--accent-subtle-border)'
            }}>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                💡 <strong style={{ color: 'var(--text-primary)' }}>Batch processing saves time:</strong> Transform your content backlog in one go. Each video uses your current settings: {outputType === 'thread' ? 'Thread' : `Article (${selectedTemplate})`}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '14px', borderRadius: '10px',
                  border: '2px solid var(--border-color)', background: 'transparent',
                  color: 'var(--text-secondary)', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleProcess}
                disabled={urlCount === 0 || urlCount > 10}
                style={{
                  flex: 2, padding: '14px', borderRadius: '10px', border: 'none',
                  background: 'var(--accent-gradient)', color: 'white',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                  opacity: urlCount === 0 || urlCount > 10 ? 0.5 : 1
                }}
              >
                Start Batch Transform
              </button>
            </div>
          </>
        ) : processing ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
              width: '60px', height: '60px', border: '4px solid var(--border-color)',
              borderTopColor: 'var(--accent-primary)', borderRadius: '50%',
              animation: 'spin 1s linear infinite', margin: '0 auto 24px'
            }} />

            <div style={{ marginBottom: '24px' }}>
              <div style={{
                fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)',
                marginBottom: '8px'
              }}>
                Processing {progress.total} video{progress.total !== 1 ? 's' : ''}...
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                This may take 2-5 minutes per video. Please don't close this window.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)',
                marginBottom: '16px', textAlign: 'center'
              }}>
                ✅ Batch Complete!
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '16px', borderRadius: '10px',
                  background: 'var(--bg-secondary)', textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {progress.total}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Total</div>
                </div>
                <div style={{
                  padding: '16px', borderRadius: '10px',
                  background: 'var(--bg-secondary)', textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--success)' }}>
                    {progress.completed}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Success</div>
                </div>
                <div style={{
                  padding: '16px', borderRadius: '10px',
                  background: 'var(--bg-secondary)', textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--error)' }}>
                    {progress.failed}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Failed</div>
                </div>
              </div>

              <div style={{
                maxHeight: '300px', overflow: 'auto',
                border: '1px solid var(--border-color)', borderRadius: '10px'
              }}>
                {results.map((result, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '12px 16px', borderBottom: idx < results.length - 1 ? '1px solid var(--border-light)' : 'none',
                      display: 'flex', alignItems: 'center', gap: '12px'
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>
                      {result.success ? '✅' : '❌'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {result.success ? result.project?.name : result.url}
                      </div>
                      {!result.success && (
                        <div style={{ fontSize: '12px', color: 'var(--error)', marginTop: '2px' }}>
                          {result.error}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={onClose}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
                background: 'var(--accent-gradient)', color: 'white',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BatchProcessor;
